import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Handshake, Compass, Shield, Users, Lightbulb, Award } from 'lucide-react';
import Card3D from '../components/Card3D';
import NavigationExact from '../components/NavigationExact';
import FooterExact from '../components/FooterExact';

const AboutPage = () => {
  const navigate = useNavigate();
  const stats = [
    { value: '1,200+', label: 'BUSINESSES FUNDED' },
    { value: '5000 M+', label: 'PROPOSAL ENABLED' },
    { value: '5+', label: 'YEARS OF EXPERTISE' },
    { value: '0.12%', label: 'PAR 30: PORTFOLIO HEALTH' }
  ];

  const values = [
    { icon: Shield, title: 'Integrity', desc: 'Every invoice we finance is GST-verified. No exceptions. We don\'t cut corners on credit discipline — your capital depends on it.' },
    { icon: Users, title: 'Client Focus', desc: 'We measure our success by how fast your money moves, not how many loans we close. Your cash flow is our scorecard.' },
    { icon: Lightbulb, title: 'Innovation', desc: 'AI-driven underwriting that reads real trade data — GSTN, e-invoices, account aggregator signals — not just credit scores.' },
    { icon: Award, title: 'Excellence', desc: '0.12% PAR 30. That number is our promise to every business that trusts us with their invoice.' }
  ];

  return (
    <>
      <NavigationExact />
      <div className="pt-16">
      {/* Hero Section */}
      <section className="py-12 md:py-16 bg-gradient-to-br from-gray-50 via-white to-red-50/40">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.span 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-red-100 to-red-50 text-[#5C1F33] rounded-full text-sm font-semibold mb-8 shadow-sm"
          >
            <span className="w-2 h-2 bg-[#5C1F33] rounded-full animate-pulse"></span>
            Our Story
          </motion.span>
          
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-extrabold mb-8 leading-[1.1] tracking-tight"
          >
            About <span className="text-[#5C1F33]">Funding</span> <span className="text-[#5C1F33]">Sathi</span>
          </motion.h1>
          
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-gray-600 text-lg sm:text-xl md:text-2xl max-w-3xl mx-auto leading-relaxed font-light"
          >
            We saw thousands of Indian MSMEs sitting on verified invoices — waiting 60, 90, 120 days to get paid. We built Funding Sathi to end that wait.
          </motion.p>

          <button
            type="button"
            onClick={() => navigate('/contact')}
            className="inline-flex w-full sm:w-auto items-center justify-center gap-2 mt-10 bg-[#5C1F33] text-white px-8 py-4 rounded-xl hover:bg-[#800020] hover:shadow-xl hover:shadow-[#5C1F33]/20 transition-all duration-300 font-medium text-lg"
          >
            Get in Touch
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </button>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-[#5C1F33] font-semibold text-sm uppercase tracking-wider mb-4 block">Who We Are</span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-8 tracking-tight">
                Our <span className="text-[#5C1F33]">Story</span>
              </h2>
              <div className="space-y-5 text-gray-600 leading-[1.8] text-base md:text-lg">
                <p>
                  We started with a simple observation. Thousands of Indian suppliers had verified invoices from large, creditworthy buyers sitting unpaid for 60, 90, even 120 days. The trade was real. The anchor was strong. But the money wasn't moving.
                </p>
                <p>
                  Banks wanted collateral. NBFCs wanted margins. And the MSME in the middle waited.
                </p>
                <p>
                  Funding Sathi was built to solve exactly that gap. Not by becoming another lender, but by building the verification and structuring layer that makes every invoice bankable. We connect your verified trade with the right capital, faster than traditional financing ever could.
                </p>
                <p className="text-[#5C1F33] font-medium">
                  Today, we've enabled over ₹30,000 million in capital across anchor ecosystems, and we're just getting started.
                </p>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="absolute -inset-4 bg-gradient-to-r from-[#5C1F33]/10 to-[#800020]/10 rounded-3xl blur-2xl opacity-60"></div>
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src="/img1.png" 
                  alt="Indian Business Team Collaboration" 
                  className="w-full h-80 sm:h-96 lg:h-[32rem] object-cover"
                  loading="lazy"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-24 md:py-32 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-[#5C1F33] font-semibold text-sm uppercase tracking-wider mb-4 block">Our Purpose</span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-5 tracking-tight">Our Mission & Vision</h2>
          <p className="text-gray-500 text-lg md:text-xl mb-16 max-w-2xl mx-auto">Guiding principles that drive everything we do</p>
          
          <div className="grid md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Card3D className="h-full">
                <div className="bg-white rounded-3xl p-10 shadow-lg border border-gray-100 h-full hover:shadow-2xl hover:shadow-[#5C1F33]/10 transition-all duration-300 group">
                  <div className="mx-auto w-20 h-20 bg-gradient-to-br from-[#5C1F33] to-[#800020] rounded-2xl shadow-lg shadow-[#5C1F33]/25 flex items-center justify-center mb-8 group-hover:scale-110 group-hover:shadow-xl group-hover:shadow-[#5C1F33]/30 transition-all duration-300">
                    <Handshake className="w-10 h-10 text-white stroke-[1.5]" />
                  </div>
                  <h3 className="text-2xl font-bold mb-5 text-gray-900 text-center">Our Mission</h3>
                  <p className="text-gray-600 leading-[1.8] text-base md:text-lg text-left">
                    To build the verification and structuring infrastructure that makes every verified invoice bankable. We connect Indian MSMEs with institutional capital through our AI-driven platform, enabling fast, collateral-free funding against real trade flows.
                  </p>
                </div>
              </Card3D>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <Card3D className="h-full">
                <div className="bg-white rounded-3xl p-10 shadow-lg border border-gray-100 h-full hover:shadow-2xl hover:shadow-[#5C1F33]/10 transition-all duration-300 group">
                  <div className="mx-auto w-20 h-20 bg-gradient-to-br from-[#5C1F33] to-[#800020] rounded-2xl shadow-lg shadow-[#5C1F33]/25 flex items-center justify-center mb-8 group-hover:scale-110 group-hover:shadow-xl group-hover:shadow-[#5C1F33]/30 transition-all duration-300">
                    <Compass className="w-10 h-10 text-white stroke-[1.5]" />
                  </div>
                  <h3 className="text-2xl font-bold mb-5 text-gray-900 text-center">Our Vision</h3>
                  <p className="text-gray-600 leading-[1.8] text-base md:text-lg text-left">
                    To become India's leading supply chain finance platform, bridging the ₹30 lakh crore MSME credit gap by 2030. We envision a future where every verified trade transaction has instant access to affordable capital.
                  </p>
                </div>
              </Card3D>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#5C1F33] font-semibold text-sm uppercase tracking-wider mb-4 block">What We Stand For</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 tracking-tight">Our Core Values</h2>
            <p className="text-gray-500 text-lg md:text-xl max-w-2xl mx-auto">The principles that guide everything we do</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card3D className="h-full">
                  <div className="bg-white rounded-2xl p-8 border border-gray-100 hover:shadow-2xl hover:shadow-[#5C1F33]/10 hover:border-[#5C1F33]/20 transition-all duration-300 h-full group">
                    <div className="mx-auto w-14 h-14 bg-gradient-to-br from-[#5C1F33] to-[#800020] rounded-2xl shadow-md flex items-center justify-center mb-6 group-hover:scale-110 group-hover:shadow-lg transition-all duration-300">
                      <value.icon className="w-6 h-6 text-white stroke-[1.5]" />
                    </div>
                    <h4 className="font-bold text-xl mb-4 text-gray-900">{value.title}</h4>
                    <p className="text-gray-600 leading-relaxed">{value.desc}</p>
                  </div>
                </Card3D>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-[#5C1F33]/5 via-[#800020]/5 to-[#5C1F33]/10">
        <div className="max-w-[90rem] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white/80 backdrop-blur-xl rounded-[2rem] border border-[#5C1F33]/10 p-8 sm:p-12 lg:p-16 shadow-2xl shadow-[#5C1F33]/5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center group"
              >
                <p className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-[#5C1F33] mb-2 group-hover:scale-105 transition-transform duration-300">{stat.value}</p>
                <p className="text-gray-600 text-xs md:text-sm uppercase tracking-wider font-medium">{stat.label}</p>
              </motion.div>
            ))}
          </div>
          </div>
        </div>
      </section>


      </div>
      <FooterExact />
    </>
  );
};

export default AboutPage;







